import React, { useState, useEffect } from 'react'
import type { Lesson, LessonContentType, LessonContentFormat } from '@/types/lesson'
import { FileUpload } from '@/components/common/FileUpload'
import { RichTextEditor } from '@/components/common/RichTextEditor'

interface LessonFormData {
  title: string
  description: string
  contentType: LessonContentType
  contentUrl: string
  contentHtml: string
  contentFormat: LessonContentFormat
  durationMinutes: number
  isPreviewable: boolean
  isMandatory: boolean
}

interface LessonFormModalProps {
  lesson?: Lesson | null
  onSave: (data: LessonFormData) => Promise<void>
  onClose: () => void
  isSaving: boolean
}

export const LessonFormModal: React.FC<LessonFormModalProps> = ({
  lesson,
  onSave,
  onClose,
  isSaving,
}) => {
  const [formData, setFormData] = useState<LessonFormData>({
    title: '',
    description: '',
    contentType: 'video',
    contentUrl: '',
    contentHtml: '',
    contentFormat: 'html',
    durationMinutes: 0,
    isPreviewable: false,
    isMandatory: true,
  })
  const [errors, setErrors] = useState<Partial<Record<keyof LessonFormData, string>>>({})

  useEffect(() => {
    if (lesson) {
      setFormData({
        title: lesson.title || '',
        description: lesson.description || '',
        contentType: (lesson.contentType as LessonFormData['contentType']) || 'video',
        contentUrl: lesson.contentUrl || '',
        contentHtml: lesson.contentHtml || '',
        contentFormat: (lesson.contentFormat as LessonFormData['contentFormat']) || 'html',
        durationMinutes: lesson.durationMinutes || 0,
        isPreviewable: lesson.isPreviewable || false,
        isMandatory: lesson.isMandatory ?? true,
      })
    }
  }, [lesson])

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof LessonFormData, string>> = {}

    if (!formData.title.trim()) {
      newErrors.title = 'Judul materi wajib diisi'
    }

    if (formData.contentType === 'text') {
      if (!formData.contentUrl.trim() && !formData.contentHtml.trim()) {
        newErrors.contentUrl = 'Konten materi wajib diisi'
      }
    } else {
      if (!formData.contentUrl.trim()) {
        newErrors.contentUrl = 'URL atau file materi wajib diisi'
      }
    }

    if (formData.durationMinutes < 0) {
      newErrors.durationMinutes = 'Durasi harus lebih dari 0'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return
    await onSave(formData)
  }

  const handleFileUpload = (url: string) => {
    setFormData((prev) => ({ ...prev, contentUrl: url }))
    setErrors((prev) => ({ ...prev, contentUrl: undefined }))
  }

  const contentTypeOptions = [
    { value: 'video', label: 'Video', icon: 'play_circle', color: 'blue' },
    { value: 'pdf', label: 'PDF', icon: 'picture_as_pdf', color: 'rose' },
    { value: 'text', label: 'Artikel', icon: 'article', color: 'emerald' },
  ]

  const labelClasses = "block text-xs font-black text-slate-500 dark:text-slate-400 tracking-wider mb-2 ml-1"
  const inputClasses = "w-full px-4 py-3.5 bg-slate-50 dark:bg-white/[0.03] border border-slate-200 dark:border-white/10 focus:border-primary/50 focus:ring-4 focus:ring-primary/5 rounded-xl text-sm font-bold text-slate-900 dark:text-white transition-all outline-none placeholder:text-slate-400 placeholder:font-medium shadow-sm"

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-md animate-in fade-in duration-300" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden rounded-[24px] bg-white dark:bg-[#0B0F17] border border-slate-200 dark:border-white/10 shadow-2xl animate-in zoom-in-95 fade-in duration-300">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between p-6 border-b border-slate-100 dark:border-white/5 bg-white/50 dark:bg-white/[0.02] backdrop-blur-xl">
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
              <span className="material-symbols-outlined">{lesson ? 'edit_note' : 'add_circle'}</span>
            </div>
            <div>
              <h2 className="text-sm font-black text-slate-900 dark:text-white tracking-tight">
                {lesson ? 'Edit Materi' : 'Tambah Materi Baru'}
              </h2>
              <p className="text-xs font-bold text-slate-400 tracking-widest mt-0.5">Lengkapi detail pelajaran di bawah</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="size-10 rounded-xl flex items-center justify-center hover:bg-slate-100 dark:hover:bg-white/5 text-slate-400 hover:text-slate-900 dark:hover:text-white transition-all"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Scrollable Form Body */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          <form id="lesson-form" onSubmit={handleSubmit} className="p-8 space-y-8">
            {/* Title */}
            <div className="space-y-1">
              <label className={labelClasses}>Judul Materi <span className="text-rose-500">*</span></label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                className={`${inputClasses} ${errors.title ? 'border-rose-500 ring-rose-500/10' : ''}`}
                placeholder="Contoh: Pengenalan Dasar Desain Grafis"
              />
              {errors.title && <p className="mt-1.5 text-xs font-bold text-rose-500 ml-1">{errors.title}</p>}
            </div>

            {/* Content Type Selector */}
            <div className="space-y-3">
              <label className={labelClasses}>Tipe Konten</label>
              <div className="grid grid-cols-3 gap-3">
                {contentTypeOptions.map((option) => {
                  const isActive = formData.contentType === option.value
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, contentType: option.value as LessonContentType }))}
                      className={`group relative flex flex-col items-center gap-3 p-5 rounded-2xl border transition-all duration-300 ${
                        isActive
                          ? 'bg-primary/10 border-primary shadow-lg shadow-primary/5'
                          : 'bg-white dark:bg-white/[0.02] border-slate-200 dark:border-white/5 hover:border-slate-300 dark:hover:border-white/10'
                      }`}
                    >
                      <div className={`size-12 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110 ${
                        isActive 
                          ? 'bg-primary text-white' 
                          : 'bg-slate-100 dark:bg-white/5 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300'
                      }`}>
                        <span className="material-symbols-outlined text-[24px]">{option.icon}</span>
                      </div>
                      <span className={`text-xs font-black tracking-widest ${isActive ? 'text-primary' : 'text-slate-500 dark:text-slate-400'}`}>
                        {option.label}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Description */}
            <div className="space-y-1">
              <label className={labelClasses}>Deskripsi Singkat</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                rows={3}
                className={`${inputClasses} resize-none`}
                placeholder="Apa yang akan dipelajari di materi ini?"
              />
            </div>

            {/* Dynamic Content (Upload/Editor) */}
            <div className="space-y-1.5">
              <label className={labelClasses}>Isi Materi <span className="text-rose-500">*</span></label>
              <div className="rounded-2xl border border-slate-200 dark:border-white/10 overflow-hidden bg-slate-50/50 dark:bg-white/[0.01]">
                {formData.contentType === 'video' && (
                  <div className="p-4">
                    <FileUpload
                      type="video"
                      currentUrl={formData.contentUrl}
                      onUpload={handleFileUpload}
                      label="Upload Video Materi"
                    />
                  </div>
                )}
                {formData.contentType === 'pdf' && (
                  <div className="p-4">
                    <FileUpload
                      type="document"
                      currentUrl={formData.contentUrl}
                      onUpload={handleFileUpload}
                      label="Upload PDF Dokumentasi"
                    />
                  </div>
                )}
                {formData.contentType === 'text' && (
                  <div className="bg-white dark:bg-[#0B0F17]">
                    <RichTextEditor
                      value={formData.contentHtml}
                      onChange={(html) => setFormData((prev) => ({
                        ...prev,
                        contentHtml: html,
                        contentFormat: 'html',
                        contentUrl: html.replace(/<[^>]*>/g, '').substring(0, 500)
                      }))}
                      placeholder="Tulis materi pembelajaran di sini..."
                      minHeight="300px"
                    />
                  </div>
                )}
              </div>
              {errors.contentUrl && <p className="mt-1.5 text-xs font-bold text-rose-500 ml-1">{errors.contentUrl}</p>}
            </div>

            <div className="grid grid-cols-2 gap-6 pt-4">
              {/* Duration */}
              <div className="space-y-1">
                <label className={labelClasses}>Estimasi Waktu (Menit)</label>
                <div className="relative group">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 text-[20px]">schedule</span>
                  <input
                    type="number"
                    min="0"
                    value={formData.durationMinutes}
                    onChange={(e) => setFormData((prev) => ({ ...prev, durationMinutes: parseInt(e.target.value) || 0 }))}
                    className={`${inputClasses} pl-11`}
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Status Toggles */}
              <div className="space-y-4 pt-4">
                <div 
                  onClick={() => setFormData((prev) => ({ ...prev, isPreviewable: !prev.isPreviewable }))}
                  className="flex items-center justify-between cursor-pointer group"
                >
                  <div>
                    <span className="block text-xs font-black text-slate-700 dark:text-slate-200 tracking-wider">Akses Gratis</span>
                    <span className="text-xs text-slate-400 font-bold">Bisa dipratinjau sebelum beli</span>
                  </div>
                  <div className={`relative w-12 h-6 rounded-full transition-all duration-300 ${formData.isPreviewable ? 'bg-primary' : 'bg-slate-200 dark:bg-white/10'}`}>
                    <div className={`absolute top-1 left-1 size-4 rounded-full bg-white shadow-md transition-transform duration-300 ${formData.isPreviewable ? 'translate-x-6' : 'translate-x-0'}`} />
                  </div>
                </div>

                <div 
                  onClick={() => setFormData((prev) => ({ ...prev, isMandatory: !prev.isMandatory }))}
                  className="flex items-center justify-between cursor-pointer group"
                >
                  <div>
                    <span className="block text-xs font-black text-slate-700 dark:text-slate-200 tracking-wider">Wajib Diselesaikan</span>
                    <span className="text-xs text-slate-400 font-bold">Syarat sertifikat & progres</span>
                  </div>
                  <div className={`relative w-12 h-6 rounded-full transition-all duration-300 ${formData.isMandatory ? 'bg-primary' : 'bg-slate-200 dark:bg-white/10'}`}>
                    <div className={`absolute top-1 left-1 size-4 rounded-full bg-white shadow-md transition-transform duration-300 ${formData.isMandatory ? 'translate-x-6' : 'translate-x-0'}`} />
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Footer Actions */}
        <div className="flex-shrink-0 flex items-center justify-end gap-3 p-6 border-t border-slate-100 dark:border-white/5 bg-white/50 dark:bg-white/[0.02] backdrop-blur-xl">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="px-6 py-3 text-slate-500 dark:text-slate-400 text-sm font-black tracking-widest hover:text-slate-900 dark:hover:text-white transition-all disabled:opacity-50"
          >
            Batal
          </button>
          <button
            form="lesson-form"
            type="submit"
            disabled={isSaving}
            className="px-10 py-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-2xl text-sm font-black tracking-[0.2em] hover:scale-[1.02] active:scale-[0.98] transition-all shadow-xl shadow-slate-200 dark:shadow-none disabled:opacity-50 flex items-center gap-3"
          >
            {isSaving ? (
              <>
                <div className="animate-spin size-4 border-2 border-current border-t-transparent rounded-full" />
                Processing...
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-[18px]">done_all</span>
                {lesson ? 'Simpan Perubahan' : 'Buat Materi'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}


